/**
 * Editor de despiece de fabricación.
 *
 * Es la pantalla donde el taller captura de qué está hecha cada pieza: sus cortes,
 * sus herrajes y sus procesos. Todo lo demás —placas necesarias, metros de corte,
 * minutos de máquina y costo— se calcula solo y se recalcula al teclear.
 *
 * Está pensada para que la llene alguien del taller, no un ingeniero de software:
 * cada campo es una medida o una cantidad que se toma con el flexómetro o se cuenta.
 */

import { useState } from 'react'
import {
  calcularFabricacion, ahorroPorLote,
  MATERIALES_BASE, PROCESOS_BASE, HERRAJES_BASE
} from '../motor/fabricacion.js'

const mx = n => '$' + Math.round(n || 0).toLocaleString('es-MX')
const PROCESOS_MANUALES = ['armado', 'soldadura', 'pintura', 'sublimado', 'empaque']

const piezaVacia = () => ({ nombre: '', material: 'MDF18', largo: 500, ancho: 300, cant: 1 })

export default function Despiece({ sku, nombre, despiece, onGuardar, cfgTaller = {} }) {
  const d = despiece || { piezas: [], herrajes: [], procesos: [], aprovechamiento: 0.78 }
  const [cantidad, setCantidad] = useState(1)
  /* Con la configuración VIVA del taller (sus placas, su minuto de máquina): antes
     calculaba con `{}` y la pantalla decía «Valores de muestra» aunque el taller ya
     hubiera capturado sus costos — detectado en la refutación del 2026-08-22. */
  const r = calcularFabricacion(d, cfgTaller, cantidad)
  const lote = ahorroPorLote(d, cfgTaller, 6)

  const mutar = cambio => {
    const copia = structuredClone(d)
    cambio(copia)
    onGuardar(copia)
  }
  const num = (v, def = 0) => { const n = parseFloat(v); return Number.isNaN(n) ? def : n }

  return (
    <>
      <div className="warn">
        <b>Valores de muestra.</b> Ninguno de estos números viene del taller: son
        aproximaciones para que la captura exista y el cálculo se pueda ver funcionando.
        Todo es editable aquí mismo, y al cambiarlo se recalcula al instante.
      </div>

      {/* ---------- Piezas cortadas ---------- */}
      <div className="sec">Piezas cortadas</div>
      {(d.piezas || []).map((p, i) => (
        <div className="card" key={i}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
            <input value={p.nombre} placeholder="Nombre de la pieza" style={{ flex: 1 }}
              onChange={e => mutar(c => { c.piezas[i].nombre = e.target.value })} />
            <button className="ib sm dz" title="Quitar"
              onClick={() => mutar(c => c.piezas.splice(i, 1))}>×</button>
          </div>
          <label className="f"><span>Material</span>
            <select value={p.material} onChange={e => mutar(c => { c.piezas[i].material = e.target.value })}>
              {Object.entries(MATERIALES_BASE).map(([k, m]) =>
                <option key={k} value={k}>{m.nombre}</option>)}
            </select>
          </label>
          <div className="tres">
            <label className="f"><span>Largo (mm)</span>
              <input type="number" value={p.largo}
                onChange={e => mutar(c => { c.piezas[i].largo = num(e.target.value) })} /></label>
            <label className="f"><span>Ancho (mm)</span>
              <input type="number" value={p.ancho}
                onChange={e => mutar(c => { c.piezas[i].ancho = num(e.target.value) })} /></label>
            <label className="f"><span>Cantidad</span>
              <input type="number" value={p.cant}
                onChange={e => mutar(c => { c.piezas[i].cant = num(e.target.value, 1) })} /></label>
          </div>
          <div className="tres">
            <label className="f"><span>Cantos a lo largo</span>
              <input type="number" min="0" max="2" value={p.cantoLargo || 0}
                onChange={e => mutar(c => { c.piezas[i].cantoLargo = num(e.target.value) })} /></label>
            <label className="f"><span>Cantos a lo ancho</span>
              <input type="number" min="0" max="2" value={p.cantoAncho || 0}
                onChange={e => mutar(c => { c.piezas[i].cantoAncho = num(e.target.value) })} /></label>
            <label className="f"><span>Calado extra (m)</span>
              <input type="number" step="0.5" value={p.corteExtraM || 0}
                onChange={e => mutar(c => { c.piezas[i].corteExtraM = num(e.target.value) })} /></label>
          </div>
          <div className="s">
            El <b>calado extra</b> son los metros de trayectoria que el corte recorre además del
            contorno. Es lo que hace que una celosía cueste diez veces más de máquina que un
            panel liso del mismo material.
          </div>
        </div>
      ))}
      <button className="ib w" onClick={() => mutar(c => c.piezas.push(piezaVacia()))}>
        Agregar pieza
      </button>

      {/* ---------- Herrajes ---------- */}
      <div className="sec">Herrajes y consumibles</div>
      {(d.herrajes || []).map((h, i) => (
        <div className="card" key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select value={h.sku} style={{ flex: 1 }}
            onChange={e => mutar(c => { c.herrajes[i].sku = e.target.value })}>
            {Object.entries(HERRAJES_BASE).map(([k, x]) =>
              <option key={k} value={k}>{x.nombre} · {mx(x.costo)} / {x.unidad}</option>)}
          </select>
          <input type="number" step="0.1" value={h.cant} style={{ width: 90 }}
            onChange={e => mutar(c => { c.herrajes[i].cant = num(e.target.value) })} />
          <button className="ib sm dz" onClick={() => mutar(c => c.herrajes.splice(i, 1))}>×</button>
        </div>
      ))}
      <button className="ib w"
        onClick={() => mutar(c => c.herrajes.push({ sku: 'HER-TORNILLO', cant: 1 }))}>
        Agregar herraje
      </button>

      {/* ---------- Procesos declarados ---------- */}
      <div className="sec">Procesos de mano</div>
      <div className="s" style={{ margin: '0 4px 8px' }}>
        El corte y el canteado no se capturan: se calculan de las piezas. Aquí van solo los
        minutos de banco que alguien tiene que estimar.
      </div>
      {(d.procesos || []).map((p, i) => (
        <div className="card" key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select value={p.proceso} style={{ flex: 1 }}
            onChange={e => mutar(c => { c.procesos[i].proceso = e.target.value })}>
            {PROCESOS_MANUALES.map(k =>
              <option key={k} value={k}>{PROCESOS_BASE[k].nombre} · {mx(PROCESOS_BASE[k].costoMinuto)}/min</option>)}
          </select>
          <input type="number" value={p.minutos} style={{ width: 90 }}
            onChange={e => mutar(c => { c.procesos[i].minutos = num(e.target.value) })} />
          <button className="ib sm dz" onClick={() => mutar(c => c.procesos.splice(i, 1))}>×</button>
        </div>
      ))}
      <button className="ib w"
        onClick={() => mutar(c => c.procesos.push({ proceso: 'armado', minutos: 15 }))}>
        Agregar proceso
      </button>

      <div className="card" style={{ marginTop: 14 }}>
        <div className="two">
          <label className="f" style={{ margin: 0 }}><span>Aprovechamiento de placa</span>
            <input type="number" step="0.01" min="0.3" max="1" value={d.aprovechamiento || 0.78}
              onChange={e => mutar(c => { c.aprovechamiento = num(e.target.value, 0.78) })} /></label>
          <label className="f" style={{ display: 'flex', alignItems: 'center', gap: 10, margin: 0 }}>
            <input type="checkbox" checked={!!d.prorratear} style={{ width: 20, height: 20 }}
              onChange={e => mutar(c => { c.prorratear = e.target.checked })} />
            <span style={{ margin: 0 }}>Se vende por m² — prorratear placa</span>
          </label>
        </div>
      </div>

      {/* ---------- Resultado ---------- */}
      <div className="sec">Lo que resulta</div>
      <div className="card">
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
          <span className="s grow">Costo por pieza, fabricando</span>
          <div className="qty">
            <button onClick={() => setCantidad(q => Math.max(1, q - 1))}>−</button>
            <span>{cantidad}</span>
            <button onClick={() => setCantidad(q => q + 1)}>+</button>
          </div>
        </div>

        <table className="d">
          <thead><tr><th>Concepto</th><th className="n">Cant.</th><th className="n">Costo</th></tr></thead>
          <tbody>
            {r.materiales.map(m => (
              <tr key={m.clave}>
                <td>{m.nombre}
                  <div className="s">{m.area.toFixed(2)} m² · corte {m.corteM.toFixed(1)} m
                    {m.cantoM > 0 && ` · canto ${m.cantoM.toFixed(1)} m`}
                    {' · desperdicio '}{m.desperdicioPct.toFixed(0)}%</div>
                </td>
                <td className="n">{m.prorrateado ? m.placas.toFixed(2) : m.placas.toFixed(2)} placa</td>
                <td className="n">{mx(m.costo)}</td>
              </tr>
            ))}
            {r.procesos.map(p => (
              <tr key={p.clave}>
                <td>{p.nombre}<div className="s">{mx(p.costoMinuto)} por minuto</div></td>
                <td className="n">{p.minutos} min</td>
                <td className="n">{mx(p.costo)}</td>
              </tr>
            ))}
            {r.herrajes.map((h, i) => (
              <tr key={i}>
                <td>{h.nombre}</td>
                <td className="n">{h.cant} {h.unidad}</td>
                <td className="n">{mx(h.costo)}</td>
              </tr>
            ))}
            <tr>
              <td colSpan={2} style={{ fontWeight: 500, borderTop: '2px solid var(--negro)' }}>
                Costo de fabricación por pieza</td>
              <td className="n" style={{ fontWeight: 500, borderTop: '2px solid var(--negro)' }}>
                {mx(r.costoTotal)}</td>
            </tr>
          </tbody>
        </table>

        <div className="ln" style={{ marginTop: 10 }}>
          <span>Minutos totales</span><b>{r.minutosTotal}</b>
        </div>
        <div className="ln">
          <span>De ellos, de máquina</span><b>{r.minutosMaquina}</b>
        </div>
        <div className="s" style={{ marginTop: 6 }}>
          El minuto de máquina es el recurso escaso del taller, no el dinero. Por eso se
          reporta aparte del minuto de banco.
        </div>
      </div>

      {/* ---------- El argumento del lote ---------- */}
      {!d.prorratear && lote.bajaPct > 0.5 && (
        <div className="card" style={{ borderColor: 'var(--verde)' }}>
          <div className="sec" style={{ margin: '0 0 8px', color: 'var(--verde)' }}>
            El efecto del anidado
          </div>
          <div className="ln"><span>Fabricando una suelta</span><b>{mx(lote.costoUnitarioSuelto)}</b></div>
          <div className="ln"><span>Fabricando seis</span><b>{mx(lote.costoUnitarioEnLote)} por pieza</b></div>
          <div className="ln big">
            <span style={{ color: 'var(--negro)', fontWeight: 500 }}>Baja</span>
            <b>{lote.bajaPct.toFixed(1)}%</b>
          </div>
          <div className="s" style={{ marginTop: 8, lineHeight: 1.45 }}>
            Seis piezas sueltas gastarían {lote.placasSueltas} placas; anidadas en el mismo
            pedido, {lote.placasEnLote}. Ése es el argumento económico de fabricar por lotes,
            y por eso una línea modular con medidas repetidas cuesta menos que la misma pieza
            hecha a medida cada vez.
          </div>
        </div>
      )}
    </>
  )
}
